import { useEffect, useState } from 'react';

import {
  downloadAccountExport,
  downloadProjectExport,
  exportFailureMessage,
  saveExportFile,
  workspaceExportUrl,
} from '../../api/exports';
import { listProjects, type Project } from '../../api/projects';
import { listWorkspaces, type Workspace } from '../../api/workspaces';

export interface DataExportState {
  /** `null` while loading or when the list could not be read. */
  projects: Project[] | null;
  workspaces: Workspace[] | null;
  /** The id of the download in progress, or `''`. */
  busy: string;
  /** Per-control failure text, keyed by `project:<id>`, `account` or `load`. */
  errors: Record<string, string>;
  /** Set after "Download everything" built the account archive, to point at the workspace links. */
  accountDone: boolean;
}

export interface DataExportViewProps {
  state: DataExportState;
  onDownloadProject: (project: Project) => void;
  onDownloadEverything: () => void;
}

function workspaceLink(workspace: Workspace): string | null {
  try {
    return workspaceExportUrl(workspace.workspace_id, workspace.ingress_host);
  } catch {
    return null;
  }
}

/** The pure view: everything the section shows, from state, so it renders without a network. */
export function DataExportView({ state, onDownloadProject, onDownloadEverything }: DataExportViewProps) {
  const links = (state.workspaces || [])
    .map((workspace) => ({ workspace, href: workspaceLink(workspace) }))
    .filter((item): item is { workspace: Workspace; href: string } => item.href !== null);
  return (
    <section className="settings-panel-content" aria-labelledby="data-export-title">
      <h3 id="data-export-title">Data and export</h3>
      <p>
        Your work is yours. You can download a copy of it whenever you like, including if you cancel or your
        subscription ends.
      </p>
      {state.errors.load ? <p className="error" role="alert">{state.errors.load}</p> : null}

      <h4>Download your project</h4>
      <p className="settings-muted">
        Your Story, Prep, Launch and Learning files are kept in your workspace. Open your workspace once in this
        browser, then use the link to download them as one zip.
      </p>
      {links.length === 0 ? (
        <p className="settings-muted">
          {state.workspaces === null ? 'Checking your workspace…' : 'You do not have a workspace to download from yet.'}
        </p>
      ) : (
        <ul className="settings-account-links" aria-label="Workspace downloads">
          {links.map(({ workspace, href }) => (
            <li key={workspace.workspace_id}>
              {/* A real navigation from this page: the workspace needs the Referer and the user's click.
                  Never rel="noreferrer" here. */}
              <a href={href} rel="noopener" referrerPolicy="origin" target="_self">
                Download your project
              </a>{' '}
              <span className="settings-muted">(<code>{workspace.ingress_host}</code>)</span>
            </li>
          ))}
        </ul>
      )}
      {links.length > 0 ? (
        <p className="settings-muted">
          If the link says you are not signed in, choose Open workspace on the Projects page first, then come back.
        </p>
      ) : null}

      <h4>Download everything</h4>
      <p className="settings-muted">
        Builds a zip of your account records and everything stored for you outside the workspace (tool outputs,
        hosted-task outputs, uploads). It does not contain your workspace files, which come from the link above.
        It never contains provider keys.
      </p>
      <button type="button" disabled={Boolean(state.busy)} onClick={onDownloadEverything}>
        {state.busy === 'account' ? 'Preparing…' : 'Download everything'}
      </button>
      {state.errors.account ? <p className="error" role="alert">{state.errors.account}</p> : null}
      {state.accountDone ? (
        <p role="status">
          Your account export was saved. {links.length > 0 ? 'Use the Download your project link above for your workspace files.' : ''}
        </p>
      ) : null}

      {state.projects && state.projects.length > 0 ? (
        <>
          <h4>Per-project account records</h4>
          <ul className="settings-account-links" aria-label="Project exports">
            {state.projects.map((project) => (
              <li key={project.project_id}>
                <button
                  type="button"
                  className="secondary"
                  disabled={Boolean(state.busy)}
                  onClick={() => onDownloadProject(project)}
                >
                  {state.busy === `project:${project.project_id}` ? 'Preparing…' : `Download ${project.display_name}`}
                </button>
                {state.errors[`project:${project.project_id}`] ? (
                  <p className="error" role="alert">{state.errors[`project:${project.project_id}`]}</p>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

export function DataExportPanel() {
  const [state, setState] = useState<DataExportState>({
    projects: null,
    workspaces: null,
    busy: '',
    errors: {},
    accountDone: false,
  });

  useEffect(() => {
    let active = true;
    void Promise.allSettled([listProjects(), listWorkspaces()]).then(([projects, workspaces]) => {
      if (!active) return;
      setState((current) => ({
        ...current,
        projects: projects.status === 'fulfilled' ? projects.value : [],
        workspaces: workspaces.status === 'fulfilled' ? workspaces.value : [],
        errors: {
          ...current.errors,
          ...(projects.status === 'rejected' && workspaces.status === 'rejected'
            ? { load: 'Your projects and workspace could not be loaded.' }
            : {}),
        },
      }));
    });
    return () => {
      active = false;
    };
  }, []);

  async function run(key: string, work: () => Promise<void>, errorKey: string) {
    setState((current) => ({ ...current, busy: key, accountDone: false, errors: { ...current.errors, [errorKey]: '' } }));
    try {
      await work();
    } catch (failure) {
      setState((current) => ({ ...current, errors: { ...current.errors, [errorKey]: exportFailureMessage(failure) } }));
    } finally {
      setState((current) => ({ ...current, busy: '' }));
    }
  }

  return (
    <DataExportView
      state={state}
      onDownloadProject={(project) =>
        void run(`project:${project.project_id}`, async () => {
          saveExportFile(await downloadProjectExport(project.project_id, project.slug));
        }, `project:${project.project_id}`)
      }
      onDownloadEverything={() =>
        void run('account', async () => {
          saveExportFile(await downloadAccountExport());
          setState((current) => ({ ...current, accountDone: true }));
        }, 'account')
      }
    />
  );
}
